/**
 * Wishlist Module with Shopify Analytics Integration
 * Handles add/remove items, localStorage persistence, and analytics tracking
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    this.updateAllUI();
    this.attachEventListeners();
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error loading wishlist:', e);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId);
  }

  addToWishlist(productData) {
    if (this.isInWishlist(productData.id)) {
      return false;
    }

    this.wishlist.push(productData);
    this.saveWishlist();
    this.updateAllUI();
    
    // Track with Shopify Analytics
    this.trackAnalyticsEvent('added_to_wishlist', productData);
    
    // Show notification
    this.showNotification(`${productData.title} added to wishlist`);
    
    return true;
  }

  removeFromWishlist(productId) {
    const item = this.wishlist.find(item => item.id === productId);
    if (!item) return false;

    this.wishlist = this.wishlist.filter(item => item.id !== productId);
    this.saveWishlist();
    this.updateAllUI();
    
    // Track with Shopify Analytics
    this.trackAnalyticsEvent('removed_from_wishlist', item);
    
    // Show notification
    this.showNotification(`${item.title} removed from wishlist`);
    
    return true;
  }

  toggleWishlist(productData) {
    if (this.isInWishlist(productData.id)) {
      return this.removeFromWishlist(productData.id);
    } else {
      return this.addToWishlist(productData);
    }
  }

  getWishlist() {
    return this.wishlist;
  }

  getWishlistCount() {
    return this.wishlist.length;
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
    this.updateAllUI();
    this.showNotification('Wishlist cleared');
  }

  // Shopify Analytics Integration
  trackAnalyticsEvent(eventType, productData) {
    if (typeof window.ShopifyAnalytics === 'undefined') {
      console.warn('Shopify Analytics not available');
      return;
    }

    try {
      const analyticsData = {
        event: eventType,
        data: {
          product_id: productData.id,
          product_title: productData.title,
          product_handle: productData.handle,
          product_url: productData.url,
          variant_id: productData.variantId,
          price: productData.price,
          currency: window.Shopify?.currency?.active || 'USD',
          timestamp: new Date().toISOString()
        }
      };

      // Send custom event to Shopify Analytics
      if (window.ShopifyAnalytics.lib) {
        window.ShopifyAnalytics.lib.track(eventType, analyticsData.data);
      }

      // Also track with analytics.track if available
      if (typeof analytics !== 'undefined' && analytics.track) {
        analytics.track(eventType, analyticsData.data);
      }

      console.log('Analytics tracked:', eventType, analyticsData);
    } catch (e) {
      console.error('Error tracking analytics:', e);
    }
  }

  // UI Update Methods
  updateAllUI() {
    this.updateHeaderCount();
    this.updateWishlistButtons();
    this.updateWishlistPage();
  }

  updateHeaderCount() {
    const countElements = document.querySelectorAll('.wishlist-count');
    const count = this.getWishlistCount();
    
    countElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  updateWishlistButtons() {
    const buttons = document.querySelectorAll('.wishlist-button');
    
    buttons.forEach(button => {
      const productId = button.dataset.productId;
      const isActive = this.isInWishlist(productId);
      
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive);
      
      const icon = button.querySelector('.wishlist-icon');
      if (icon) {
        icon.innerHTML = isActive ? this.getFilledHeartSVG() : this.getOutlineHeartSVG();
      }

      // Update text for product page button
      const textSpan = button.querySelector('.wishlist-text');
      if (textSpan) {
        textSpan.textContent = isActive ? 'Remove from Wishlist' : 'Add to Wishlist';
      }
    });
  }

  updateWishlistPage() {
    const wishlistContainer = document.querySelector('.wishlist-items-container');
    if (!wishlistContainer) return;

    if (this.wishlist.length === 0) {
      wishlistContainer.innerHTML = `
        <div class="wishlist-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
          <h2>Your wishlist is empty</h2>
          <p>Save your favorite products here</p>
          <a href="/collections/all" class="button">Continue Shopping</a>
        </div>
      `;
      return;
    }

    const itemsHTML = this.wishlist.map(item => this.createWishlistItemHTML(item)).join('');
    wishlistContainer.innerHTML = `
      <div class="wishlist-header">
        <h2>Wishlist (${this.wishlist.length})</h2>
        <button class="wishlist-clear-all button button--secondary">Clear All</button>
      </div>
      <div class="wishlist-grid">
        ${itemsHTML}
      </div>
    `;

    // Attach clear all event
    const clearAllBtn = wishlistContainer.querySelector('.wishlist-clear-all');
    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', () => {
        if (confirm('Are you sure you want to clear your entire wishlist?')) {
          this.clearWishlist();
        }
      });
    }
  }

  createWishlistItemHTML(item) {
    // Coming soon products list (hardcoded for checking)
    const comingSoonHandles = [
      'double-loop-shirt-olive-moss',
      'double-loop-shirt-mushroom',
      'double-loop-shirt-light-blue',
      'buggy-shirt-plum-wine',
      'buggy-shirt-earth-brown',
      'buggy-shirt-cloud-white'
    ];

    // Check if product is coming soon (check both saved flag and handle)
    const isComingSoon = item.comingSoon === true || item.comingSoon === 'true' || comingSoonHandles.includes(item.handle);

    /* console.log('Product:', item.title);
    console.log('Handle:', item.handle);
    console.log('Coming Soon Flag:', item.comingSoon);
    console.log('Is Coming Soon:', isComingSoon); */

    // Price or Coming Soon badge
    const priceHTML = isComingSoon 
      ? '<div class="product-badge coming-soon-badge">Coming Soon</div>'
      : `<div class="price">
          <div class="price__container">
            <div class="price__regular">
              <span class="price-item price-item--regular">
                ${item.priceFormatted}
              </span>
            </div>
          </div>
        </div>`;

    return `
      <div class="card-wrapper product-card-wrapper underline-links-hover" data-product-id="${item.id}">
        <div class="card card--standard card--media">
          <div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: 150%;">
            <div class="card__media">
              <div class="media media--transparent media--hover-effect">
                <img
                  srcset="${item.image}&width=165 165w,
                          ${item.image}&width=360 360w,
                          ${item.image}&width=533 533w,
                          ${item.image}&width=720 720w,
                          ${item.image}&width=940 940w"
                  src="${item.image}&width=533"
                  sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                  alt="${item.title}"
                  loading="lazy"
                  class="motion-reduce"
                  width="533"
                  height="800"
                >
              </div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a href="${item.url}" class="full-unstyled-link">
                  ${item.title}
                </a>
              </h3>
              <div class="card-information">
                ${priceHTML}
              </div>
            </div>
            <div class="wishlist-item-actions">
              <button 
                class="wishlist-remove-button button button--secondary button--full-width"
                data-product-id="${item.id}"
                aria-label="Remove from wishlist"
              >
                Remove from Wishlist
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Delegate event for wishlist buttons
    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target.closest('.wishlist-button');
      if (wishlistBtn) {
        e.preventDefault();
        this.handleWishlistButtonClick(wishlistBtn);
      }

      const removeBtn = e.target.closest('.wishlist-remove-button');
      if (removeBtn) {
        e.preventDefault();
        const productId = removeBtn.dataset.productId;
        this.removeFromWishlist(productId);
      }
    });
  }

  handleWishlistButtonClick(button) {
    // Get coming soon value and convert to boolean
    const comingSoonValue = button.dataset.comingSoon;
    const isComingSoon = comingSoonValue === 'true' || comingSoonValue === true;

    /* console.log('Button data-coming-soon:', comingSoonValue);
    console.log('Converted to boolean:', isComingSoon); */

    const productData = {
      id: button.dataset.productId,
      title: button.dataset.productTitle,
      handle: button.dataset.productHandle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      priceFormatted: button.dataset.productPriceFormatted,
      variantId: button.dataset.variantId || '',
      comingSoon: isComingSoon
    };

    /* console.log('Product data being saved:', productData); */

    this.toggleWishlist(productData);
  }

  showNotification(message) {
    // Remove existing notification
    const existing = document.querySelector('.wishlist-notification');
    if (existing) existing.remove();

    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => notification.classList.add('show'), 10);
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  getOutlineHeartSVG() {
    return `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
    `;
  }

  getFilledHeartSVG() {
    return `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
    `;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}